'use client';

/**
 * /contacto — Página pública de contacto (SPEC-139 P-222 Opción A).
 *
 * Página receptora de los 3 links del footer "SOPORTE + COMUNIDAD":
 *   - "Centro de Ayuda"     → /contacto
 *   - "Status del Sistema"  → /contacto?type=status
 *   - "Reportar un Bug"     → /contacto?type=bug
 *
 * Form fields (5): nombre, email, asunto, tipo (general/status/bug),
 * mensaje. Validación frontend (HTML5 + JS) duplica el schema Zod del
 * backend (`contacto.controller.ts`) para feedback inmediato. La
 * validación AUTHORITATIVE es la del backend.
 *
 * Endpoint: POST /api/contacto (público, 5/hr/IP). El envío real de
 * email a soporte@profileia.com requiere PENDIENTE-FOUNDER #26 (SMTP).
 * Mientras tanto, el endpoint loguea estructurado + responde 200.
 *
 * Spec ref: docs/SPEC-139-QA-SITE-SPRINT63-P222-P227.md §4 (Footer fix).
 *
 * §6 Anti-Invención: 0 lógica de negocio inventada — solo validación de
 * input + submit HTTP. El rate limit (5/hr/IP) viene del SPEC §4.
 *
 * Anti-P-023: el form renderiza feedback real (loading/success/error).
 * Anti-P-041: 0 `as any` en este archivo.
 * Anti-P-042: 0 `console.*` en este archivo.
 */
import { useState, useEffect, Suspense, type FormEvent } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import { sendContactoMessage, type ContactoTipo } from '@/lib/contacto-api';
import { ApiClientError } from '@/lib/api-client';

const TIPO_LABELS: Record<ContactoTipo, string> = {
  general: 'Consulta general',
  status: 'Estado del sistema',
  bug: 'Reporte de bug',
};

// C-P3-2 (Sprint 93 QA): placeholder dinámico del asunto según tipo.
// Mejora UX dando ejemplos concretos en lugar de uno genérico.
const ASUNTO_PLACEHOLDERS: Record<ContactoTipo, string> = {
  general: 'Resumen corto de tu consulta',
  status: 'Ej: ¿Está caído el login?',
  bug: 'Ej: Error 500 al comprar paquete Bronce',
};

export default function ContactoPage() {
  return (
    <Suspense
      fallback={
        <main id="main-content" className="min-h-screen bg-slate-950 text-slate-100">
          <section className="border-b border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950">
            <div className="container mx-auto px-4 py-12 max-w-3xl">
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-3">
                Contáctanos
              </h1>
              <p className="text-slate-300">Cargando formulario…</p>
            </div>
          </section>
        </main>
      }
    >
      <ContactoContent />
    </Suspense>
  );
}

function ContactoContent() {
  const searchParams = useSearchParams();

  // P-222 §4: el `?type=status|bug` query param pre-selecciona el dropdown.
  // Si no viene o es inválido, default a 'general'.
  const queryType = searchParams.get('type');
  const initialTipo: ContactoTipo =
    queryType === 'status' || queryType === 'bug' ? queryType : 'general';

  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [asunto, setAsunto] = useState('');
  const [tipo, setTipo] = useState<ContactoTipo>(initialTipo);
  const [mensaje, setMensaje] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [feedback, setFeedback] = useState('');

  // Si el usuario llega a /contacto?type=bug, pre-llenar el asunto para
  // reducir fricción (UX sugerida por SPEC-139 §4 AC-3).
  useEffect(() => {
    if (initialTipo === 'bug' && !asunto) {
      setAsunto('Reporte de bug — ');
    } else if (initialTipo === 'status' && !asunto) {
      setAsunto('Consulta sobre estado del sistema — ');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTipo]);

  function validate(): string | null {
    if (nombre.trim().length < 2) return 'Tu nombre debe tener al menos 2 caracteres.';
    if (email.trim().length === 0) return 'El email es obligatorio.';
    // Simple email regex — el backend valida con Zod (authoritative).
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!emailOk) return 'El email no es válido.';
    if (asunto.trim().length < 3) return 'El asunto debe tener al menos 3 caracteres.';
    if (mensaje.trim().length < 20) return 'El mensaje debe tener al menos 20 caracteres.';
    return null;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setStatus('error');
      setFeedback(validationError);
      return;
    }

    setStatus('loading');
    setFeedback('');

    try {
      const result = await sendContactoMessage({
        nombre: nombre.trim(),
        email: email.trim(),
        asunto: asunto.trim(),
        tipo,
        mensaje: mensaje.trim(),
      });
      setStatus('success');
      setFeedback(result.message);
      // Limpieza del form post-envío exitoso.
      setNombre('');
      setEmail('');
      setAsunto('');
      setMensaje('');
    } catch (err) {
      setStatus('error');
      if (err instanceof ApiClientError) {
        setFeedback(err.message || 'No pudimos enviar tu mensaje. Intenta de nuevo.');
      } else {
        setFeedback('No pudimos enviar tu mensaje. Intenta de nuevo más tarde.');
      }
    }
  }

  return (
    <main id="main-content" className="min-h-screen bg-slate-950 text-slate-100">
      <section className="border-b border-slate-800 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto px-4 py-12 max-w-3xl">
          <Link
            href="/"
            className="inline-flex items-center text-slate-400 hover:text-cyan-300 transition-colors text-sm mb-6"
          >
            <ArrowLeft className="size-4 mr-1" /> Volver al inicio
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-3">
            Contáctanos
          </h1>
          <p className="text-slate-300 leading-relaxed">
            ¿Tienes una consulta, quieres reportar un bug o verificar el estado
            del sistema? Completa el formulario y nuestro equipo te responderá
            por email a la brevedad.
          </p>
        </div>
      </section>

      <section className="border-b border-slate-800">
        <div className="container mx-auto px-4 py-12 max-w-2xl">
          {status === 'success' ? (
            <Alert className="bg-emerald-950/40 border-emerald-700 text-emerald-200">
              <CheckCircle2 className="size-4" />
              <AlertDescription>
                {feedback}
              </AlertDescription>
            </Alert>
          ) : null}

          {status === 'error' ? (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertDescription>{feedback}</AlertDescription>
            </Alert>
          ) : null}

          <form onSubmit={handleSubmit} className="space-y-6 mt-6" noValidate>
            <div className="space-y-2">
              <Label htmlFor="nombre" className="text-slate-200">
                Nombre <span className="text-red-400">*</span>
              </Label>
              <Input
                id="nombre"
                type="text"
                autoFocus
                aria-label="Nombre completo (obligatorio, mínimo 2 caracteres)"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Tu nombre completo"
                maxLength={80}
                required
                minLength={2}
                className="bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-200">
                Email <span className="text-red-400">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                aria-label="Email (obligatorio)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@email.com"
                maxLength={254}
                required
                className="bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tipo" className="text-slate-200">
                Tipo de consulta <span className="text-red-400">*</span>
              </Label>
              <select
                id="tipo"
                aria-label="Tipo de consulta (obligatorio)"
                value={tipo}
                onChange={(e) => setTipo(e.target.value as ContactoTipo)}
                required
                className="w-full px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {(['general', 'status', 'bug'] as const).map((t) => (
                  <option key={t} value={t}>
                    {TIPO_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="asunto" className="text-slate-200">
                Asunto <span className="text-red-400">*</span>
              </Label>
              <Input
                id="asunto"
                type="text"
                aria-label="Asunto (obligatorio, mínimo 3 caracteres)"
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
                placeholder={ASUNTO_PLACEHOLDERS[tipo]}
                maxLength={120}
                required
                minLength={3}
                className="bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="mensaje" className="text-slate-200">
                Mensaje <span className="text-red-400">*</span>
              </Label>
              <Textarea
                id="mensaje"
                aria-label="Mensaje (obligatorio, mínimo 20 caracteres, máximo 4000)"
                aria-describedby="mensaje-counter"
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                placeholder={
                  tipo === 'bug'
                    ? 'Describe el bug: pasos para reproducirlo, qué esperabas, qué pasó...'
                    : tipo === 'status'
                      ? '¿Qué servicio o endpoint quieres verificar?'
                      : 'Tu mensaje...'
                }
                rows={6}
                maxLength={4000}
                required
                minLength={20}
                className="bg-slate-900 border-slate-700 text-slate-100 placeholder-slate-500"
              />
              <p id="mensaje-counter" className="text-xs text-slate-500" aria-live="polite">
                {mensaje.length}/4000 caracteres · mínimo 20.
              </p>
            </div>

            <Button
              type="submit"
              disabled={status === 'loading'}
              className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-800 disabled:cursor-not-allowed text-white w-full sm:w-auto"
            >
              {status === 'loading' ? (
                <>
                  <Loader2 className="size-4 mr-2 animate-spin" />
                  Enviando…
                </>
              ) : (
                'Enviar mensaje'
              )}
            </Button>

            <p className="text-xs text-slate-500 leading-relaxed pt-2">
              Limitamos el envío a 5 mensajes por hora por IP para evitar spam.
              Para soporte prioritario, escríbenos a{' '}
              <a
                href="mailto:soporte@profileia.com"
                className="text-emerald-400 hover:text-emerald-300 underline"
              >
                soporte@profileia.com
              </a>
              .
            </p>
          </form>
        </div>
      </section>
    </main>
  );
}
